import {
  ADR,
  ADR_EDIT,
  ADR_REVIEW,
  PROJCOMP,
  CEM4601,
  CEM4907,
} from "../_constants/actionConstants";
import "../_typedefs/adrV3TypeDefs";
import {
  WorkPerformedFactory,
  LocationWorkPerformedFactory,
  LaborSetFactory,
  EquipmentSetFactory,
  AdrFactory,
  GeneralCommentsFactory,
  CorrectionCommentFactory,
  ClarificationCommentFactory,
  TentativeAgreementsFactory,
} from "./helpers/adrFactories";

const adrInitialState = {
  openAdrs: {}, // Array of all open Adr's. Normalized by tabId.
  openAdrLists: {}, // Normalized by projectId.
  adrSearchResultsList: [], // Collection of all Adr searches made by user. See below for shape.
  tempFilePaths: {}, // Files to be deleted when closing tab. Normalized by tabId
  miscSessionData: {
    amendmentHistory: {}, // Normalized by originalAdrIds
    // Rest are normalized by tabIds
  },
  fetching: false,
  fetchingPreview: false,
  fetchingPdf: false,
  searching: false,
  sendingEmail: false,
  error: null,
};

/*
 * Shape of openAdrs:
 * [*assistantDailyReportDto...]
 *
 * Shape of adrList:
 * {
 *  [projectId(*int)]: [{*assistantDailyReportIndexModel}...]
 * }
 *
 * Shape of adrSearchResultsList:
 * [{
 *  searchId: *int,
 *  projectId: *int,
 *  searchResults: *assistantDailyReportIndexModel
 * }...]
 *
 * Shape of searchParams:
 * {
 *  projectId: *int,
 *  singleDate: *moment,
 *  dateRange: [{ dateFrom: *moment, dateTo: *moment }...],
 *  reportNos: [*int],
 *  authorId: *int,
 *  commentKeywordIds: [*int],
 *  locationIds: [*int],
 *  itemIds: [*int],
 *  ccoIds: [*int],
 *  laborIds: [*int],
 *  equipmentIds: [*int]
 * }
 */

export const reducer = (state = adrInitialState, action) => {
  switch (action.type) {
    case ADR.AMEND:
    case ADR.SAVE_ADR:
    case ADR.FETCH_ADR:
    case ADR.FETCH_ADR_LIST:
    case ADR.DELETE_ADR:
    case ADR.ADR_DELETE_ATTACHMENT:
    case ADR.GET_NEXT_TO_REVIEW:
    case ADR.FETCH_ALL_ADR_DATES:
    case ADR.FETCH_COUNT_OF_SAFETY_COMMENTS:
    case ADR.FETCH_TENT_AGREEMENT:
    case ADR.DOWNLOAD_ATTACHMENTS:
    case ADR.FETCH_CORRECTION_COMMENTS:
    case ADR.FETCH_CLARIFICATION_COMMENTS:
    case ADR_EDIT.WORKPERFORMED.DELETE:
      return { ...state, fetching: true };

    case CEM4601.REQUEST:
    case CEM4907.REQUEST:
      return { ...state, fetchingPdf: true };

    case ADR.FETCH_ADR_SEARCH:
      return { ...state, searching: true };

    case ADR.FETCH_ADR_SEARCH_REJECTED:
      return { ...state, searching: false };

    case ADR.FETCH_PREVIEW:
      return { ...state, fetchingPreview: true };

    case ADR_EDIT.TENTATIVE_AGREEMENTS.SEND_EMAIL:
      return { ...state, sendingEmail: true };

    case ADR_EDIT.TENTATIVE_AGREEMENTS.SEND_EMAIL_FULFILLED:
    case ADR_EDIT.TENTATIVE_AGREEMENTS.SEND_EMAIL_REJECTED:
      return { ...state, sendingEmail: false };

    case ADR.CREATE:
    case ADR.CREATEV2_FULFILLED:
    case ADR.AMEND_FULFILLED: {
      const {
        tabId,
        adrDto,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList,
      } = action;
      if (!tabId || !adrDto) return { ...state };

      const adrFactory = new AdrFactory();
      adrFactory.initClient(
        adrDto,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList
      );
      adrFactory.setIsCopy(false);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adrFactory.data },
        fetching: false,
      };
    }

    case ADR.FETCH_ADR_FULFILLED: {
      const {
        tabId,
        adrDto,
        adrAmendHistory,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList,
      } = action;
      // tabId and adrDto are required, adrAmendHistory is optional.
      if (!tabId || !adrDto) return { ...state };

      const adrFactory = new AdrFactory();
      adrFactory.initClient(
        adrDto,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList
      );
      if (adrAmendHistory) adrFactory.setAmendHistory(adrAmendHistory);
      adrFactory.setIsCopy(false);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adrFactory.data },
        fetching: false,
      };
    }

    case ADR.FETCH_AMEND_HISTORY_FULFILLED: {
      const { originalAdrId, amendmentHistory } = action;
      if (!originalAdrId || !amendmentHistory) return { ...state };

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          amendmentHistory: {
            ...("amendmentHistory" in state.miscSessionData
              ? state.miscSessionData.amendmentHistory
              : {}),
            [originalAdrId]: amendmentHistory,
          },
        },
      };
    }

    case ADR.COPY_FULFILLED: {
      const {
        adrDto,
        tabId,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList,
      } = action;
      if (!adrDto || !tabId) return { ...state };

      const adrFactory = new AdrFactory(adrDto);
      adrFactory.initClient(
        adrDto,
        projItemList,
        projCcoList,
        projPendingIssueList,
        projLaborList,
        projEquipList
      );

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adrFactory.data },
      };
    }

    case ADR.COPY_ON_CLIENT: {
      const { tabIdOfOriginal, tabIdOfCopy } = action;
      if (!tabIdOfOriginal || !tabIdOfCopy) return { ...state };

      const adrToCopy = state.openAdrs[tabIdOfOriginal];
      if (!adrToCopy) return { ...state };

      const adrFactory = new AdrFactory(adrToCopy);
      adrFactory.copyClient();

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabIdOfCopy]: adrFactory.data },
      };
    }

    case ADR.FETCH_ADR_LIST_FULFILLED: {
      const { tabId, adrList, includeFrom } = action;
      if (!tabId) return { ...state, fetching: false };
      return {
        ...state,
        openAdrLists: {
          ...state.openAdrLists,
          [tabId]: { adrList, includeFrom, isSearchResult: false },
        },
        fetching: false,
      };
    }

    case ADR.SAVE_ADR_FULFILLED: {
      // Update the openAdr with new adrId (when editing, the adrId
      // received back from the /Edit API will be the same.)
      const { tabId, adrId } = action;
      const adr = { ...state.openAdrs[tabId] };
      if (!adr) return { ...state };

      adr.assistantDailyReportId = adrId;

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adr },
        fetching: false,
      };
    }

    case ADR.CREATE_FULFILLED:
    case ADR.COPY_ADR_FULFILLED:
    case ADR.CREATE_REJECTED:
    case ADR.AMEND_REJECTED:
    case ADR.COPY_ADR_REJECTED:
    case ADR.FETCH_ADR_REJECTED:
    case ADR.FETCH_ADR_LIST_REJECTED:
    case ADR.SAVE_ADR_REJECTED:
    case ADR.GET_NEXT_TO_REVIEW_FULFILLED:
    case ADR.GET_NEXT_TO_REVIEW_REJECTED:
    case ADR.ADR_DELETE_ATTACHMENT_FULFILLED:
    case ADR.ADR_DELETE_ATTACHMENT_REJECTED:
    case ADR.DELETE_ADR_FULFILLED:
    case ADR.FETCH_COUNT_OF_SAFETY_COMMENTS_FULFILLED:
    case ADR.FETCH_COUNT_OF_SAFETY_COMMENTS_REJECTED:
    case ADR.FETCH_TENT_AGREEMENT_REJECTED:
    case ADR.DOWNLOAD_ATTACHMENTS_FULFILLED:
    case ADR.DOWNLOAD_ATTACHMENTS_REJECTED:
    case ADR_EDIT.WORKPERFORMED.DELETE_REJECTED:
    case ADR.FETCH_CORRECTION_COMMENTS_REJECTED:
    case ADR.FETCH_CLARIFICATION_COMMENTS_REJECTED:
      return { ...state, fetching: false };

    case CEM4601.REQUEST_REJECTED:
    case CEM4907.REQUEST_REJECTED:
    case CEM4601.REQUEST_FULFILLED:
    case CEM4907.REQUEST_FULFILLED:
      return { ...state, fetchingPdf: false };

    case ADR.FETCH_PREVIEW_FULFILLED:
    case ADR.FETCH_PREVIEW_REJECTED:
      return { ...state, fetchingPreview: false };

    case ADR.CLOSE_ADR: {
      // When an Adr tab is closed, remove Adr from state.openAdrs without saving.
      const openAdrs = { ...state.openAdrs };
      delete openAdrs[action.tabId];
      return { ...state, openAdrs, fetching: false };
    }

    case ADR.UPDATE_STATUS: {
      const { tabId, status } = action;

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...state.openAdrs[tabId],
            ...status,
          },
        },
      };
    }

    case ADR.ADR_DELETE_TEMP_ATTACHMENT_FULFILLED: {
      const tempFilePaths = { ...state.tempFilePaths };
      delete tempFilePaths[action.tabId];
      return { ...state, tempFilePaths, fetching: false };
    }

    case ADR.ADR_ADD_TEMP_FILEPATHS: {
      const { tabId, filePaths } = action;
      const existTempFilePaths =
        tabId in state.tempFilePaths ? [...state.tempFilePaths[tabId]] : [];
      return {
        ...state,
        tempFilePaths: {
          ...state.tempFilePaths,
          [tabId]: [...existTempFilePaths, ...filePaths],
        },
        fetching: false,
      };
    }

    case ADR.ADR_CLEAR_TEMP_FILEPATHS: {
      // Same as ADR_DELETE_ATTACHMENT_FULFILLED.
      // Dispatched when Adr is to be saved
      const tempFilePaths = { ...state.tempFilePaths };
      delete tempFilePaths[action.tabId];
      return { ...state, tempFilePaths, fetching: false };
    }

    case ADR.FETCH_ADR_SEARCH_FULFILLED: {
      return {
        ...state,
        openAdrLists: {
          ...state.openAdrLists,
          [action.tabId]: {
            includeFrom: null, // includeFrom filter is disabled for search results
            isSearchResult: true,
            adrList: action.adrList,
            searchParams: action.searchParams,
          },
        },
        searching: false,
      };
    }

    case ADR.FETCH_TENT_AGREEMENT_FULFILLED: {
      const { tabId, tentativeAgreements } = action;
      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...state.openAdrs[tabId],
            tentativeAgreements,
          },
        },
      };
    }

    case ADR.FETCH_CORRECTION_COMMENTS_FULFILLED: {
      const { tabId, correctionComments } = action;
      if (!tabId) return { ...state };

      const ccFactory = new CorrectionCommentFactory();
      ccFactory.initClient(correctionComments);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...state.openAdrs[tabId],
            correctionComments: ccFactory.data,
          },
        },
      };
    }

    case ADR.FETCH_CLARIFICATION_COMMENTS_FULFILLED: {
      const { tabId, clarificationComments } = action;
      if (!tabId) return { ...state };

      const ccFactory = new ClarificationCommentFactory();
      ccFactory.initClient(clarificationComments);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...state.openAdrs[tabId],
            clarificationComments: ccFactory.data,
          },
        },
      };
    }

    case ADR.CLOSE_ADR_LIST:
    case ADR.CLOSE_SEARCH: {
      const openAdrLists = { ...state.openAdrLists };
      delete openAdrLists[action.tabId];
      return { ...state, openAdrLists, fetching: false };
    }

    case ADR.CLEAR_ALL_ADR_DATA:
      return adrInitialState;

    /************************************************************************************
     * Adr Edit Actions
     ***********************************************************************************/

    case ADR_EDIT.STATUS: {
      const { tabId, statusToUpdate, value } = action;
      if (!tabId || value == null) return { ...state };
      // Pre-screen front-end settable status flags only.
      if (!["isDraft", "isSubmitted", "isDeleted"].includes(statusToUpdate))
        return { ...state };
      const adr = { ...state.openAdrs[tabId] }; // Shallow copy
      if (!adr) return { ...state };

      adr[statusToUpdate] = value;

      return { ...state, openAdrs: { ...state.openAdrs, [tabId]: adr } };
    }

    case ADR_EDIT.HEADER.UPDATE: {
      const { tabId, headerFields } = action;
      if (!tabId || !headerFields) return { ...state };

      const adr = { ...state.openAdrs[tabId] }; // Shallow copy
      if (!adr) return { ...state };

      for (const field in headerFields) {
        if (field in adr) adr[field] = headerFields[field];
      }

      return { ...state, openAdrs: { ...state.openAdrs, [tabId]: adr } };
    }

    /**
     * Adds new workPerformed, initialized as follows:
     * - One blank locationWorkPerformed added
     * -
     */
    case ADR_EDIT.WORKPERFORMED.ADD: {
      const { tabId, newWp } = action;
      if (!tabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const wps = new WorkPerformedFactory(adr.workPerformedClient);
      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);

      wps.add(newWp);
      laborSetClient.addCol(wps.newWpTabId);
      equipmentSetClient.addCol(wps.newWpTabId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            workPerformedClient: wps.data,
            newWpTabId: wps.newWpTabId,
            laborSetClient: laborSetClient.data,
            equipmentSetClient: equipmentSetClient.data,
          },
        },
      };
    }

    /**
     * Deletes workPerformed.
     * LocationWorkPerformedAttachments must first be deleted prior to this action.
     * @actionparam {Number} tabId
     * @actionparam {Number} wpTabId
     */
    case ADR_EDIT.WORKPERFORMED.DELETE_FULFILLED: {
      const { tabId, wpTabId } = action;
      if (!tabId || !wpTabId) return { ...state, fetching: false };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state, fetching: false };

      const wPs = new WorkPerformedFactory(adr.workPerformedClient);
      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);

      laborSetClient.deleteCol(wpTabId);
      equipmentSetClient.deleteCol(wpTabId);
      wPs.delete(wpTabId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            workPerformedClient: wPs.data,
            laborSetClient: laborSetClient.data,
            equipmentSetClient: equipmentSetClient.data,
          },
        },
        fetching: false,
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.ADD: {
      const { tabId, wpTabId, newLocWp } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = null; // null, but set doNotCopy=true
      const doNotCopy = true;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy, // null
        doNotCopy // true
      );

      locWps.add(newLocWp);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.DELETE: {
      const { tabId, wpTabId, clientId } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = null; // Copy all

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.delete(clientId);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.UPDATE: {
      const { tabId, wpTabId, clientId, fields } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.update(clientId, fields);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.ADD: {
      const { tabId, wpTabId, clientId, uploadedFiles } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );
      const wpId = wps.data[wpTabId].workPerformedId;

      locWps.addAttachment(clientId, wpId, uploadedFiles);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.TOGGLE_DELETE: {
      const { tabId, wpTabId, clientId, locWpaIndex } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.toggleDeleteAttachment(clientId, locWpaIndex);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.ATTACHMENTS.UPDATE: {
      const { tabId, wpTabId, clientId, locWpaIndex, fields } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.updateAttachment(clientId, locWpaIndex, fields);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.MISC_MATERIALS.ADD: {
      const { tabId, wpTabId, clientId } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );
      const wpId = wps.data[wpTabId].workPerformedId;

      locWps.addMiscMaterials(clientId, wpId);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.MISC_MATERIALS.DELETE: {
      const { tabId, wpTabId, clientId, locMmIndex } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.deleteMiscMaterials(clientId, locMmIndex);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.MISC_MATERIALS.UPDATE: {
      const { tabId, wpTabId, clientId, locMmIndex, fields } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.updateMiscMaterials(clientId, locMmIndex, fields);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.TESTS_PERFORMED.ADD: {
      const { tabId, wpTabId, clientId } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );
      const wpId = wps.data[wpTabId].workPerformedId;

      locWps.addTestPerformed(clientId, wpId);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.TESTS_PERFORMED.DELETE: {
      const { tabId, wpTabId, clientId, locTpIndex } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.deleteTestPerformed(clientId, locTpIndex);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    case ADR_EDIT.LOCATION_WORKPERFORMED.TESTS_PERFORMED.UPDATE: {
      const { tabId, wpTabId, clientId, locTpIndex, fields } = action;
      if (!tabId || !wpTabId) return { ...state };
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const clientIdToCopy = clientId;

      const wps = new WorkPerformedFactory(adr.workPerformedClient, wpTabId);
      const locWps = new LocationWorkPerformedFactory(
        wps.data[wpTabId].locationWorkPerformed,
        clientIdToCopy
      );

      locWps.updateTestPerformed(clientId, locTpIndex, fields);
      wps.updateLocationWorkPerformed(wpTabId, locWps.data);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workPerformedClient: wps.data },
        },
      };
    }

    /**
     * LABORSET.ADD - adds new labor row
     * @actionparam {number} tabId
     * @actionparam {number=} insertAt - Optional, 0-based row insert position
     */
    case ADR_EDIT.LABORSET.ADD: {
      const { tabId, insertAt, rowsToAdd, laborData } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const wps = new WorkPerformedFactory(adr.workPerformedClient);
      const laborSetClient = new LaborSetFactory(adr.laborSetClient);

      if (!laborData)
        laborSetClient.addBlankRows(wps.getAllTabIds(), insertAt, rowsToAdd);
      else laborSetClient.addRows(wps.getAllTabIds(), insertAt, laborData);

      const laborSetOrder = laborSetClient.getRowOrder();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, laborSetClient: laborSetClient.data, laborSetOrder },
        },
      };
    }

    /**
     * LABORSET.DELETE - delete labor row
     * @actionparam {number} tabId
     * @actionparam {number} rowIndex - 0-based row index to mark for deletion
     */
    case ADR_EDIT.LABORSET.DELETE: {
      const { tabId, rowKey } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[action.tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      laborSetClient.deleteRow(rowKey);

      const laborSetOrder = laborSetClient.getRowOrder();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, laborSetClient: laborSetClient.data, laborSetOrder },
        },
      };
    }

    case ADR_EDIT.LABORSET.REORDER: {
      const { tabId, newLaborSetOrder: newLso } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      laborSetClient.resetRowOrder(newLso);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, laborSetClient: laborSetClient.data, laborSetOrder: newLso },
        },
      };
    }

    case ADR_EDIT.LABORSET.UPDATE: {
      const { tabId, rowKey, fields } = action;
      if (!tabId || !rowKey || !fields) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient, rowKey);
      laborSetClient.update(rowKey, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.LABORSET.APPLY_HOURS_FROM_TA: {
      const { tabId, tentativeAgreement: ta } = action;
      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient, null, false);
      laborSetClient.applyChangesFromTentativeAgreement(ta);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.LABORSET.SHOW_ALL_OT: {
      const { tabId } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      laborSetClient.showAllOvertimeRows();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.LABORSET.HIDE_ALL_OT: {
      const { tabId } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient);
      laborSetClient.hideAllOvertimeRows();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.LABORSET.REMARKS.UPDATE: {
      const { tabId, rowKey, remarks } = action;
      if (!tabId || !rowKey) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient, rowKey);
      laborSetClient.updateRemarks(rowKey, remarks);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.LABORSET.HOURS.UPDATE: {
      const { tabId, rowKey, wpTabId, fields } = action;
      if (!tabId || !rowKey || wpTabId == null) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const laborSetClient = new LaborSetFactory(adr.laborSetClient, rowKey);
      laborSetClient.updateHours(rowKey, wpTabId, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [action.tabId]: { ...adr, laborSetClient: laborSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.ADD: {
      const { tabId, insertAt, rowsToAdd, equipmentData } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const wps = new WorkPerformedFactory(adr.workPerformedClient);
      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);

      if (!equipmentData)
        equipmentSetClient.addBlankRows(wps.getAllTabIds(), insertAt, rowsToAdd);
      else equipmentSetClient.addRows(wps.getAllTabIds(), insertAt, equipmentData);

      const equipmentSetOrder = equipmentSetClient.getRowOrder();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            equipmentSetClient: equipmentSetClient.data,
            equipmentSetOrder,
          },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.DELETE: {
      const { tabId, rowKey } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[action.tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);
      equipmentSetClient.deleteRow(rowKey);

      const equipmentSetOrder = equipmentSetClient.getRowOrder();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            equipmentSetClient: equipmentSetClient.data,
            equipmentSetOrder,
          },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.REORDER: {
      const { tabId, newEquipmentSetOrder: newEso } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);
      equipmentSetClient.resetRowOrder(newEso);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            equipmentSetClient: equipmentSetClient.data,
            equipmentSetOrder: newEso,
          },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.UPDATE: {
      const { tabId, rowKey, fields } = action;
      if (!tabId || !rowKey || !fields) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient, rowKey);
      equipmentSetClient.update(rowKey, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.APPLY_HOURS_FROM_TA: {
      const { tabId, tentativeAgreement: ta } = action;
      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(
        adr.equipmentSetClient,
        null,
        false
      );
      equipmentSetClient.applyChangesFromTentativeAgreement(ta);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.SHOW_ALL_OT: {
      const { tabId } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);
      equipmentSetClient.showAllOvertimeRows();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.HIDE_ALL_OT: {
      const { tabId } = action;
      if (!tabId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);
      equipmentSetClient.hideAllOvertimeRows();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.REMARKS.UPDATE: {
      const { tabId, rowKey, remarks } = action;
      if (!tabId || !rowKey) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient, rowKey);
      equipmentSetClient.updateRemarks(rowKey, remarks);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.HOURS.UPDATE: {
      const { tabId, rowKey, wpTabId, fields } = action;
      if (!tabId || !rowKey || wpTabId == null) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient, rowKey);
      equipmentSetClient.updateHours(rowKey, wpTabId, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    case ADR_EDIT.EQUIPMENTSET.OPERATOR.UPDATE: {
      const { tabId, rowKey, operatorData } = action;
      if (!tabId || !rowKey) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient, rowKey);
      equipmentSetClient.updateOperators(rowKey, operatorData);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
        },
      };
    }

    // MULTIPLE OPERATORS: FOR FUTURE USE
    // case ADR_EDIT.EQUIPMENTSET.OPERATOR.UPDATE: {
    //   const { tabId, rowKey, operatorData } = action;

    //   const { tabId, rowKey, operatorData } = action;
    //   if (!tabId || rowKey == null) return { ...state };

    //   /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
    //   const adr = state.openAdrs[tabId];
    //   if (!adr) return { ...state };

    //   const equipmentSetClient = new EquipmentSetFactory(adr.equipmentSetClient);
    //   equipmentSetClient.updateOperator(rowKey, operatorData);

    //   return {
    //     ...state,
    //     openAdrs: {
    //       ...state.openAdrs,
    //       [tabId]: { ...adr, equipmentSetClient: equipmentSetClient.data },
    //     },
    //   };
    // }

    case ADR_EDIT.GENERAL_COMMENTS.ADD: {
      const { tabId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const genComments = new GeneralCommentsFactory(adr.generalComments);
      genComments.addBlankRow();

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, generalComments: genComments.data },
        },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.DELETE: {
      const { tabId, clientId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.delete(clientId);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.UPDATE: {
      const { tabId, clientId, gcFields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.update(clientId, gcFields);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.KEYWORDS.UPDATE: {
      const { tabId, clientId, keywordIds } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.updateKeywords(clientId, keywordIds);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.TOGGLE_DELETE: {
      const { tabId, clientId, gcaIndex } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.toggleDeleteAttachment(clientId, gcaIndex);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.ADD: {
      const { tabId, clientId, uploadedFiles } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.addAttachment(clientId, uploadedFiles);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.GENERAL_COMMENTS.ATTACHMENTS.UPDATE: {
      const { tabId, clientId, gcaIndex, fields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const gc = new GeneralCommentsFactory(adr.generalComments);
      gc.updateAttachment(clientId, gcaIndex, fields);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, generalComments: gc.data } },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.ADD_BLANK: {
      const { tabId, wpTabId, contractorId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr || !wpTabId) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.addBlank(adr, wpTabId, contractorId);

      const newSessionData = { ...adr.sessionData, newTaClientId: tas.newTaClientId };

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data, sessionData: newSessionData },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.UPDATE: {
      const { tabId, clientId, fields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.update(clientId, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.SAVE: {
      const { tabId, tentativeAgreement: ta, isNew } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.save(ta, isNew);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: {
            ...adr,
            tentativeAgreements: tas.data,
          },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.DELETE: {
      const { tabId, clientId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.delete(clientId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.ADD_TEMP_FILEPATHS: {
      const { tabId, clientId, filePaths } = action;
      if (!tabId || !clientId) return { ...state };

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...(state.miscSessionData[tabId] || {}),
            tentativeAgreementTempFilePaths: {
              [clientId]: [
                ...(((state.miscSessionData[tabId] || {})
                  .tentativeAgreementTempFilePaths || {})[clientId] || []),
                ...(filePaths || []),
              ],
            },
          },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.CLEAR_TEMP_FILEPATHS: {
      const { tabId, clientId } = action;
      if (
        !tabId ||
        !clientId ||
        !(tabId in state.miscSessionData) ||
        !("tentativeAgreementTempFilePaths" in state.miscSessionData[tabId]) ||
        !(clientId in state.miscSessionData[tabId].tentativeAgreementTempFilePaths)
      ) {
        return { ...state };
      }

      const taTempFilePaths = _.cloneDeep(
        state.miscSessionData[tabId].tentativeAgreementTempFilePaths
      );

      delete taTempFilePaths[clientId];

      return {
        ...state,
        miscSessionData: {
          ...state.miscSessionData,
          [tabId]: {
            ...(state.miscSessionData[tabId] || {}),
            tentativeAgreementTempFilePaths: taTempFilePaths,
          },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.CONTRACTORS.ADD: {
      const { tabId, clientId, contractorId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.addContractor(clientId, contractorId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.CONTRACTORS.DELETE: {
      const { tabId, clientId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.delete(clientId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data },
        },
      };
    }

    case ADR_EDIT.TENTATIVE_AGREEMENTS.CONTRACTORS.UPDATE: {
      const { tabId, clientId, contractorId, fields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const tas = new TentativeAgreementsFactory(adr.tentativeAgreements);
      tas.updateContractor(clientId, contractorId, fields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, tentativeAgreements: tas.data },
        },
      };
    }

    case PROJCOMP.FETCH_WORKDAY_NUMBER_FULFILLED: {
      const { tabId, workdayNumber } = action;
      const adr = state.openAdrs[tabId];
      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, workdayNumber: workdayNumber },
        },
      };
    }

    /************************************************************************************
     * Adr Review Actions
     ***********************************************************************************/

    case ADR_REVIEW.APPROVE:
    case ADR_REVIEW.REJECT:
    case ADR_REVIEW.SAVE:
    case ADR_REVIEW.START:
      return { ...state, fetching: true };

    case ADR_REVIEW.REJECT_REJECTED:
    case ADR_REVIEW.APPROVE_REJECTED:
    case ADR_REVIEW.REJECT_FULFILLED:
    case ADR_REVIEW.SAVE_REJECTED:
    case ADR_REVIEW.SAVE_FULFILLED:
    case ADR_REVIEW.START_REJECTED:
      return { ...state, fetching: false };

    // Revoke and StartReview share the same functionalities.
    // When revoking,
    case ADR_REVIEW.REVOKE: {
      const { tabId, newReviewerUserId, newReviewStartedOn } = action;
      if (!tabId || !newReviewerUserId) return { ...state };

      /** @type {import("../_typedefs/adrV3TypeDefs").AssistantDailyReportClient} */
      const adr = { ...state.openAdrs[tabId] };
      if (!adr) return { ...state };

      adr.reviewerUserId = newReviewerUserId;
      adr.reviewStartedOn = newReviewStartedOn;
      adr.isReviewRevoked = true;

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adr },
        fetching: false,
      };
    }

    case ADR.RETRACT_SUBMIT_FULFILLED: {
      const { tabId } = action;
      if (!tabId) return { ...state };

      const adr = { ...state.openAdrs[tabId] };
      adr.reviewerUserId = null;
      adr.isSubmitted = false;
      adr.submittedOn = null;
    }

    case ADR_REVIEW.START_FULFILLED: {
      const { tabId, reviewerUserId, reviewStartedOn, revokeReview } = action;
      if (!tabId || !reviewerUserId) return { ...state };

      const adr = { ...state.openAdrs[tabId] };
      adr.reviewerUserId = reviewerUserId;
      adr.isInReview = true;
      adr.reviewStartedOn = reviewStartedOn;
      adr.isReviewRevoked = revokeReview;

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adr },
        fetching: false,
      };
    }

    case ADR_REVIEW.APPROVE_FULFILLED: {
      const { tabId } = action;
      const adr = { ...state.openAdrs[tabId] };
      if (!adr) return { ...state };

      adr.isApproved = true;

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: adr },
        fetching: false,
      };
    }

    case ADR_REVIEW.CORRECTION_COMMENTS.ADD_BLANK: {
      const { tabId, byUserId, section, fieldInfo } = action;
      const adr = state.openAdrs[tabId];
      if (!adr || !byUserId) return { ...state };

      const cc = new CorrectionCommentFactory(adr.correctionComments);
      cc.addBlankRow(adr.assistantDailyReportId, byUserId, section, fieldInfo);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, correctionComments: cc.data } },
      };
    }

    case ADR_REVIEW.CORRECTION_COMMENTS.UPDATE: {
      const { tabId, clientId, ccFields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const cc = new CorrectionCommentFactory(adr.correctionComments);
      cc.update(clientId, ccFields);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, correctionComments: cc.data } },
      };
    }

    case ADR_REVIEW.CORRECTION_COMMENTS.DELETE: {
      const { tabId, clientId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const cc = new CorrectionCommentFactory(adr.correctionComments);
      cc.delete(clientId);

      return {
        ...state,
        openAdrs: { ...state.openAdrs, [tabId]: { ...adr, correctionComments: cc.data } },
      };
    }

    case ADR_REVIEW.CLARIFICATION_COMMENTS.ADD_BLANK: {
      const { tabId, byUserId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr || !byUserId) return { ...state };

      const cc = new ClarificationCommentFactory(adr.clarificationComments);
      cc.addBlankRow(adr.assistantDailyReportId, byUserId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, clarificationComments: cc.data },
        },
      };
    }

    case ADR_REVIEW.CLARIFICATION_COMMENTS.UPDATE: {
      const { tabId, clientId, ccFields } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const cc = new ClarificationCommentFactory(adr.clarificationComments);
      cc.update(clientId, ccFields);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, clarificationComments: cc.data },
        },
      };
    }

    case ADR_REVIEW.CLARIFICATION_COMMENTS.KEYWORDS.UPDATE: {
      const { tabId, clientId, keywordIds } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const cc = new ClarificationCommentFactory(adr.clarificationComments);
      cc.updateKeywords(clientId, keywordIds);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, clarificationComments: cc.data },
        },
      };
    }

    case ADR_REVIEW.CLARIFICATION_COMMENTS.DELETE: {
      const { tabId, clientId } = action;
      const adr = state.openAdrs[tabId];
      if (!adr) return { ...state };

      const cc = new ClarificationCommentFactory(adr.clarificationComments);
      cc.delete(clientId);

      return {
        ...state,
        openAdrs: {
          ...state.openAdrs,
          [tabId]: { ...adr, clarificationComments: cc.data },
        },
      };
    }

    default:
      return state;
  }
};
